<!--
  SupportImageAttachment.svelte — an image attachment shown as a thumbnail, with
  a full-size preview on click.

  The thumbnail is the server's small WebP rendition (`?variant=thumbnail`), so a
  thread of screenshots does not pull every original just to draw it; the
  original is fetched only when the preview opens. Both come through the
  authenticated fetch as blobs — the JWT is a header an `<img src>` cannot send —
  and their object URLs are revoked when the component goes away.

  A thumbnail that cannot be produced (a format the server does not render, or a
  file gone from storage) falls back to the plain file chip, so an image is never
  harder to get at than any other attachment.
-->
<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { Button, Modal, Spinner } from 'flowbite-svelte';
	import { fetchSupportAttachmentBlob } from '../AppNetwork';
	import type { SupportAttachment } from '../AppTypes';
	import { formatFileSize } from '../support/supportFormat';
	import { t } from '../i18n';

	interface Props {
		/** The image attachment. */
		attachment: SupportAttachment;
		/** Saves the original, as the plain file chip does. */
		ondownload: (_attachment: SupportAttachment) => void;
		/**
		 * A small square with no caption, for lists that print the filename
		 * beside it (the attachments panel). The fallback becomes an icon button.
		 */
		compact?: boolean;
		/** The only image of its message: a wider 16:9 crop rather than the grid's 16:10. */
		wide?: boolean;
	}

	let { attachment, ondownload, compact = false, wide = false }: Props = $props();

	/** Object URL of the thumbnail, once loaded. */
	let thumbnailURL = $state<string | null>(null);
	/** Whether the thumbnail could not be loaded — show the plain chip instead. */
	let thumbnailFailed = $state(false);
	/** Whether the preview is open. */
	let previewOpen = $state(false);
	/** Object URL of the original, once loaded for the preview. */
	let fullURL = $state<string | null>(null);
	/** Whether the original failed to load. */
	let fullFailed = $state(false);

	/** Loads the thumbnail. */
	onMount(async () => {
		/** A failure only costs the picture; the chip still offers the file. */
		try {
			thumbnailURL = URL.createObjectURL(await fetchSupportAttachmentBlob(attachment.attachmentUUID, 'thumbnail'));
		} catch {
			thumbnailFailed = true;
		}
	});

	/** Opens the preview, loading the original the first time. */
	async function openPreview(): Promise<void> {
		previewOpen = true;
		/* Loaded once and kept: reopening the same image should be instant. */
		if (fullURL || fullFailed) return;
		/** A failed original leaves the thumbnail on screen with the download offered. */
		try {
			fullURL = URL.createObjectURL(await fetchSupportAttachmentBlob(attachment.attachmentUUID));
		} catch {
			fullFailed = true;
		}
	}

	onDestroy(() => {
		/* Blob URLs pin their bytes in memory until revoked. */
		if (thumbnailURL) URL.revokeObjectURL(thumbnailURL);
		if (fullURL) URL.revokeObjectURL(fullURL);
	});
</script>

{#if thumbnailFailed && compact}
	<button
		type="button"
		class="bg-depth depth-hover flex h-12 w-12 items-center justify-center rounded-lg"
		onclick={() => ondownload(attachment)}
		title={$t('Download {{name}}', { name: attachment.filename })}
		aria-label={$t('Download {{name}}', { name: attachment.filename })}
	>
		<i class="fa-solid fa-image"></i>
	</button>
{:else if thumbnailFailed}
	<button
		type="button"
		class="bg-depth depth-hover inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs"
		onclick={() => ondownload(attachment)}
		title={$t('Download {{name}}', { name: attachment.filename })}
	>
		<i class="fa-solid fa-image"></i>
		<span class="font-medium break-all">{attachment.filename}</span>
		<span class="text-default">{formatFileSize(attachment.size)}</span>
	</button>
{:else if compact}
	<button
		type="button"
		class="group focus-visible:ring-primary-500 relative block h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/5 transition focus-visible:ring-2 focus-visible:outline-none dark:bg-gray-800 dark:ring-white/10"
		onclick={openPreview}
		title={$t('Preview {{name}}', { name: attachment.filename })}
		data-testid="image-attachment"
	>
		{#if thumbnailURL}
			<img
				src={thumbnailURL}
				alt={attachment.filename}
				class="h-full w-full object-cover object-top transition duration-200 group-hover:scale-105"
			/>
		{:else}
			<span class="block h-full w-full animate-pulse bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
		{/if}
	</button>
{:else}
	<!-- A card the screenshot fills — the full width it is given, cropped from the
	     top where a screenshot's context is — with the name on a gradient over its
	     foot so the tile stays one clean shape. Hover lifts it and shows a zoom
	     cue: it opens the original. -->
	<button
		type="button"
		class="group focus-visible:ring-primary-500 relative block w-full overflow-hidden rounded-xl bg-gray-100 shadow-sm ring-1 ring-black/5 transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none dark:bg-gray-800 dark:ring-white/10 {wide
			? 'aspect-video'
			: 'aspect-[16/10]'}"
		onclick={openPreview}
		title={$t('Preview {{name}}', { name: attachment.filename })}
		data-testid="image-attachment"
	>
		{#if thumbnailURL}
			<img
				src={thumbnailURL}
				alt={attachment.filename}
				class="absolute inset-0 h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.03]"
			/>
		{:else}
			<span class="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
		{/if}
		<span
			class="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100"
			aria-hidden="true"
		>
			<span class="flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm">
				<i class="fa-solid fa-magnifying-glass-plus text-sm"></i>
			</span>
		</span>
		<span
			class="absolute inset-x-0 bottom-0 flex items-end gap-2 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-2.5 pt-6 pb-1.5 text-left"
		>
			<span class="min-w-0 flex-1 truncate text-[11px] font-medium text-white">{attachment.filename}</span>
			<span class="shrink-0 text-[10px] text-white/70">{formatFileSize(attachment.size)}</span>
		</span>
	</button>
{/if}

<Modal bind:open={previewOpen} title={attachment.filename} size="xl" outsideclose>
	<div class="flex min-h-40 items-center justify-center">
		{#if fullURL}
			<img src={fullURL} alt={attachment.filename} class="max-h-[70vh] max-w-full rounded-lg object-contain" />
		{:else if fullFailed}
			<p class="text-default text-sm">{$t('The full image could not be loaded.')}</p>
		{:else}
			<Spinner size="8" />
		{/if}
	</div>
	{#snippet footer()}
		<div class="flex w-full items-center gap-2">
			<span class="text-default text-sm">{formatFileSize(attachment.size)}</span>
			<Button class="ms-auto" onclick={() => ondownload(attachment)}>
				<i class="fa-solid fa-download me-2"></i>{$t('Download')}
			</Button>
		</div>
	{/snippet}
</Modal>
